import { Box, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import {
  Column,
  CustomTableComponent,
} from "../../../shared/elements/customTable.component";
import { PagerComponent } from "../../../shared/elements/pager.component";
import { CustomButtonComponent } from "../../../shared/elements/cutomBtn.component";
import { CustomDialogComponent } from "../../../shared/elements/customDialog.component";
import { UserAccountStatus, UserRow } from "../dto/index.dto";
import { UserDetailsResComponent } from "./user-details-res.component";
import {
  useGetUsersHook,
  useReactivateUserHook,
  useSuspendUserHook,
} from "../hooks/users.hook";

export interface UsersProps {
  // Define any props needed for the users component
}

type SortKey =
  | "fullName"
  | "birthDate"
  | "email"
  | "badgesCount"
  | "totalReservations";
type SortDir = "asc" | "desc";

export const UsersComponent: React.FC<UsersProps> = () => {
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useGetUsersHook(page);
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>(
    null
  );

  const [statusOverrides, setStatusOverrides] = useState<
    Record<number, UserAccountStatus>
  >({});
  const [actionErrors, setActionErrors] = useState<Record<number, string>>(
    {}
  );
  const [pendingSuspendUserId, setPendingSuspendUserId] = useState<
    number | null
  >(null);
  const [suspendDialogError, setSuspendDialogError] = useState<string | null>(
    null
  );

  const suspendMutation = useSuspendUserHook();
  const reactivateMutation = useReactivateUserHook();

  const getStatus = (userId: number): UserAccountStatus =>
    statusOverrides[userId] ?? "verified";

  const handleReactivate = (userId: number) => {
    setActionErrors((prev) => ({ ...prev, [userId]: "" }));
    reactivateMutation.mutate(userId.toString(), {
      onSuccess: (res) =>
        setStatusOverrides((prev) => ({ ...prev, [userId]: res.status })),
      onError: (err: any) =>
        setActionErrors((prev) => ({
          ...prev,
          [userId]:
            err?.response?.data?.message || "Une erreur est survenue.",
        })),
    });
  };

  const handleConfirmSuspend = () => {
    if (pendingSuspendUserId == null || suspendMutation.isPending) return;
    const userId = pendingSuspendUserId;
    setSuspendDialogError(null);
    suspendMutation.mutate(userId.toString(), {
      onSuccess: (res) => {
        setStatusOverrides((prev) => ({ ...prev, [userId]: res.status }));
        setPendingSuspendUserId(null);
      },
      onError: (err: any) =>
        setSuspendDialogError(
          err?.response?.data?.message ||
            "Une erreur est survenue lors de la suspension."
        ),
    });
  };

  const toggleSort = (key: SortKey) => {
    setSort((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  const sortedRows = useMemo(() => {
    const rows = (data?.data || []).map((user) => ({
      ...user,
      birthDateDisplay: user.birthDate
        ? new Date(user.birthDate).toLocaleDateString()
        : "—",
    }));
    if (!sort) return rows;
    const { key, dir } = sort;
    const sign = dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (key === "birthDate") {
        return (
          (new Date(a.birthDate).getTime() -
            new Date(b.birthDate).getTime()) *
          sign
        );
      }
      if (key === "fullName" || key === "email") {
        return a[key].localeCompare(b[key]) * sign;
      }
      return (a[key] - b[key]) * sign;
    });
  }, [data, sort]);

  const columns: Column[] = [
    {
      key: "fullName",
      label: "Nom complete ↑↓",
      sortable: true,
      onSort: () => toggleSort("fullName"),
    },
    {
      key: "birthDateDisplay",
      label: "Date de naissance ↑↓",
      sortable: true,
      onSort: () => toggleSort("birthDate"),
    },
    {
      key: "email",
      label: "Adresse email ↑↓",
      sortable: true,
      onSort: () => toggleSort("email"),
    },
    {
      key: "badgesCount",
      label: "Nombre des badges ↑↓",
      sortable: true,
      onSort: () => toggleSort("badgesCount"),
    },
    {
      key: "totalReservations",
      label: "Nombre réservation ↑↓",
      sortable: true,
      onSort: () => toggleSort("totalReservations"),
    },
    {
      key: "status",
      label: "Statut",
      render: (_, row) => {
        const status = getStatus(row.id);
        const isBlocked = status === "blocked";
        return (
          <Typography
            fontWeight="600"
            fontSize="0.78rem"
            sx={{ color: isBlocked ? "#c0392b" : "#2e7d32" }}
          >
            {isBlocked ? "Suspendu" : "Actif"}
          </Typography>
        );
      },
    },
    {
      key: "actions",
      label: "",
      render: (_, row) => {
        const status = getStatus(row.id);
        const isBlocked = status === "blocked";
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            <CustomButtonComponent
              label={isBlocked ? "Réactiver" : "Suspendre"}
              onClick={() =>
                isBlocked
                  ? handleReactivate(row.id)
                  : setPendingSuspendUserId(row.id)
              }
              disabled={
                suspendMutation.isPending || reactivateMutation.isPending
              }
              sx={{
                width: "110px",
                padding: "4px 12px",
                fontSize: "12px",
                ...(isBlocked
                  ? {}
                  : {
                      background: "transparent",
                      border: "2px solid #c0392b",
                      color: "#c0392b",
                    }),
              }}
            />
            {actionErrors[row.id] && (
              <Typography fontSize="0.65rem" color="#c0392b" mt={0.3}>
                {actionErrors[row.id]}
              </Typography>
            )}
          </Box>
        );
      },
    },
  ];

  const handleRowClick = (row: UserRow) => {
    setSelectedUser(row);
  };

  if (isLoading) {
    return <div>Loading users...</div>;
  }

  if (error) {
    return <div>Error loading users</div>;
  }

  return (
    <>
      {selectedUser ? (
        <UserDetailsResComponent
          id={selectedUser.id.toString()}
          fullName={selectedUser.fullName}
          setSelectedUser={setSelectedUser}
        />
      ) : (
        <Box>
          <CustomTableComponent
            columns={columns}
            rows={sortedRows}
            handleClick={handleRowClick}
          />
          {data && (
            <PagerComponent
              page={data.page}
              totalPages={data.totalPages}
              total={data.total}
              onPageChange={setPage}
            />
          )}
        </Box>
      )}

      {/* Suspend Confirmation Dialog */}
      <CustomDialogComponent
        open={pendingSuspendUserId !== null}
        onClose={() => {
          setPendingSuspendUserId(null);
          setSuspendDialogError(null);
        }}
        title="Suspendre l'utilisateur"
        onConfirm={handleConfirmSuspend}
        confirmLabel={suspendMutation.isPending ? "Suspension..." : "Suspendre"}
        cancelLabel="Annuler"
      >
        <Box display="flex" flexDirection="column" gap={1.5} py={1}>
          <Typography fontSize="0.85rem">
            Suspendre cet utilisateur ? Il ne pourra plus se connecter.
          </Typography>
          {suspendDialogError && (
            <Typography fontSize="0.75rem" color="#c0392b">
              {suspendDialogError}
            </Typography>
          )}
        </Box>
      </CustomDialogComponent>
    </>
  );
};
